import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CampaignImage } from "@/components/campaign-card/campaign-image";
import { CampaignSwitcherList } from "@/components/campaign-switcher-list";
import { InGameCampaignSideDock } from "@/components/in-game-campaign-side-dock";
import { SwitcherCoverStack } from "@/components/switcher-cover-stack";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { campaignHook, campaignKey, type CampaignAttention } from "@/campaigns";
import { useImageUrl } from "@/hooks/use-image-url";
import { useIsLandscapePhone } from "@/hooks/use-landscape-phone";
import { useSwitchableCampaigns } from "@/hooks/use-switchable-campaigns";
import { useSwitcherAttention } from "@/hooks/use-switcher-attention";
import { dockEnterAnimation, switcherSpring } from "@/utils/animations";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";
import { CampaignHookLine } from "@/components/campaign-hook-line";

/**
 * The in-game campaign selector on phones: a BOTTOM SHEET fixed to the frame's bottom edge.
 *
 * Full width, top corners rounded, no side gutters — the native "layer over the page" idiom.
 * It sits OUTSIDE the panel (a sibling rendered by WidgetContainer, so nothing clips it): the
 * panel keeps its side inset and its bottom edge tucks ~16px behind the sheet's top, so the
 * panel's corners visibly disappear into the sheet and the sheet's upward shadow lands on the
 * panel. The selected campaign is always visible in the bar; tapping unfolds the alternatives
 * UPWARD out of the same surface over a scrim that dims the panel. Below `lg` only — desktop
 * keeps the pill dropdown in InGameCampaignNavigation.
 *
 * Geometry contract with the panel: WidgetContainer gives the panel bottom room for the sheet
 * (`.igw-dock-room`), and `--igw-dock-inset` (index.css) tells the panel's scroll region how much
 * of it the sheet covers so the last card always clears it.
 */
export const InGameCampaignDock = () => {
  const { t } = useTranslation();
  // Sideways, the sheet has no height to live in: the dock becomes a column on the left edge.
  const isLandscape = useIsLandscapePhone();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Rotating hands over to the side dock (and back): the sheet comes back folded, and its
  // light-dismiss listeners (keyed on isOpen) come down with it.
  useEffect(() => setIsOpen(false), [isLandscape]);
  // A tapped row: the switch itself waits until the list has folded (see onExitComplete).
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const {
    selected: selectedCampaign,
    others: otherCampaigns,
    hasMultiple: hasMultipleCampaigns,
    select,
  } = useSwitchableCampaigns();
  // Hooks stay above the early return. `useImageUrl` tolerates undefined.
  const selectedCoverSrc = useImageUrl(selectedCampaign?.campaign.image);
  // News on the OTHER campaigns: a dot on their cover in the stack, and once a bubble.
  const attention = useSwitcherAttention(otherCampaigns);
  const reducedMotion = useReducedMotion();

  // First open of the session: once the panel has settled, the stack bumps and the list rises
  // just far enough to show its top row, then folds — so a player who never taps the bar still
  // learns it opens. Session-scoped (sessionStorage, guarded: it can throw or be wiped).
  const [hint, setHint] = useState<"idle" | "peek" | "done">("idle");
  useEffect(() => {
    if (!hasMultipleCampaigns || isLandscape || reducedMotion) return;
    if (readSession(HINT_KEY)) return;
    const start = window.setTimeout(() => {
      writeSession(HINT_KEY, "1");
      setHint("peek");
    }, 700);
    const end = window.setTimeout(() => setHint("done"), 1600);
    return () => {
      window.clearTimeout(start);
      window.clearTimeout(end);
    };
  }, [hasMultipleCampaigns, isLandscape, reducedMotion]);
  const peeking = hint === "peek" && !isOpen;

  // The news bubble: the first campaign with news the player has not been told about this
  // session gets one line over the stack ("Jackpot QA · Must drop now") for a few seconds, after
  // the hint. Tapping it switches straight there.
  const newsTarget = otherCampaigns.find(({ campaign }) => {
    const a = attention[campaignKey(campaign)];
    return a && !seenNews().includes(newsId(campaignKey(campaign), a));
  });
  const newsTargetKey = newsTarget ? campaignKey(newsTarget.campaign) : null;
  const [bubbleKey, setBubbleKey] = useState<string | null>(null);
  useEffect(() => {
    if (!newsTargetKey || isOpen || hint === "peek") return;
    const a = attention[newsTargetKey];
    if (!a) return;
    const show = window.setTimeout(
      () => {
        markNewsSeen(newsId(newsTargetKey, a));
        setBubbleKey(newsTargetKey);
      },
      hint === "idle" ? 1700 : 300
    );
    return () => window.clearTimeout(show);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the target or phase changes
  }, [newsTargetKey, isOpen, hint]);
  useEffect(() => {
    if (!bubbleKey) return;
    const hide = window.setTimeout(() => setBubbleKey(null), 5000);
    return () => window.clearTimeout(hide);
  }, [bubbleKey]);
  useEffect(() => {
    if (isOpen) setBubbleKey(null);
  }, [isOpen]);

  // Light-dismiss: a tap anywhere outside the dock or Escape folds the list. composedPath, not
  // contains: the widget lives in a shadow root and the listener is on the document. Escape is
  // swallowed in the capture phase so the panel's dialog doesn't ALSO close — first Escape folds
  // the list, the next one closes the widget.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = dockRef.current;
      if (el && !e.composedPath().includes(el)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setIsOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isOpen]);

  // Sequenced, not simultaneous: fold the list first, switch the campaign when the fold has
  // finished. Doing both in one frame — height animation, scrim fade, identity pop, wash
  // crossfade, old page out, new page mounting and rising — overran the frame budget and
  // read as stutter. Two short beats read as one deliberate motion.
  const handleSelect = (id: string) => {
    setPendingKey(id);
    setIsOpen(false);
  };
  const commitPendingSelect = () => {
    if (!pendingKey) return;
    select(pendingKey);
    setPendingKey(null);
  };
  // Belt and braces: the fold's exit callback is the trigger, but a tap must never be lost if it
  // doesn't fire (an unmounted or hidden list has no exit to complete) — commit after the fold's
  // duration regardless. Idempotent: whichever runs first clears the pending key.
  useEffect(() => {
    if (!pendingKey) return;
    const id = window.setTimeout(commitPendingSelect, 180);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- commit reads the latest pending key
  }, [pendingKey]);

  // Nothing to switch to → no selector at all (WidgetContainer also drops the room it reserves).
  if (!selectedCampaign || !hasMultipleCampaigns) return null;
  if (isLandscape) return <InGameCampaignSideDock />;

  const selectedIsFinished = selectedCampaign.campaign.status === "FI";
  const selectedHook = campaignHook(selectedCampaign.owned, t);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.14 }}
            onClick={() => setIsOpen(false)}
            // Dims the whole widget (panel included) under the unfolded list; darkest at the
            // bottom where the dock is (see .switcher-scrim).
            className="switcher-scrim absolute inset-0 z-60 lg:hidden"
          />
        )}
      </AnimatePresence>
      {/* Mobile campaign DOCK: a raised bar OVER the bottom of the scroll region (absolute, not a
      flex sibling) — content runs on underneath it, cards pass behind its rounded ends and its
      upward shadow lands on real content, so it reads as the layer above, not the last row.
      The selected campaign is always visible in it (cover as a faint tilted wash behind the
      title); tapping unfolds the alternatives UPWARD out of the same surface (the list is a
      child of this bar, not a portal), over the dimmed content. Thumb-reach on a phone, and
      it leaves the top row to the close button alone. */}
      <div
        ref={dockRef}
        // Reserves the closed sheet's height (bar + home-indicator safe area); the open sheet
        // grows upward out of it.
        className="absolute inset-x-0 bottom-0 z-70 h-[calc(4rem+env(safe-area-inset-bottom,0px))] lg:hidden"
      >
        <AnimatePresence>
          {bubbleKey && attention[bubbleKey] && (
            <NewsBubble
              key={bubbleKey}
              title={campaignDisplayTitle(
                otherCampaigns.find(
                  (c) => campaignKey(c.campaign) === bubbleKey
                )!.campaign,
                t
              )}
              attention={attention[bubbleKey]}
              onSelect={() => {
                setBubbleKey(null);
                select(bubbleKey);
              }}
            />
          )}
        </AnimatePresence>
        <motion.div
          variants={dockEnterAnimation}
          initial="initial"
          animate="animate"
          exit="exit"
          className="anim-layer dock-lift-shadow absolute inset-x-0 bottom-0 overflow-hidden rounded-t-3xl border border-b-0 border-white/10 bg-background-card pb-[env(safe-area-inset-bottom,0px)]"
        >
          <AnimatePresence initial={false} onExitComplete={commitPendingSelect}>
            {(isOpen || peeking) && otherCampaigns.length > 0 && (
              <CampaignSwitcherList
                campaigns={otherCampaigns}
                selectedKey={campaignKey(selectedCampaign.campaign)}
                onSelect={handleSelect}
                peek={peeking}
              />
            )}
          </AnimatePresence>
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setIsOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            className="relative isolate flex h-16 w-full min-w-0 cursor-pointer items-center gap-3.5 overflow-hidden py-3 pr-4 pl-4 text-left"
          >
            {/* The cover fills the whole bar, blurred hard so it is a colour field rather than
            readable art, under a dark gradient heaviest where the text sits (left) so title
            and timer clear AA on any cover. Scaled up so the blur's soft edges never show. */}
            <div
              className="absolute inset-0 -z-10 overflow-hidden"
              aria-hidden="true"
            >
              {/* Crossfades between campaigns (not `mode="wait"`: the old wash lingers under the
              new one so the bar never flashes bare). */}
              <AnimatePresence initial={false}>
                {selectedCoverSrc && (
                  <motion.img
                    key={selectedCoverSrc}
                    src={selectedCoverSrc}
                    alt=""
                    // Motion owns opacity here, so the wash's 0.2 target lives in the animation.
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.7 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className={cn(
                      "absolute inset-0 h-full w-full scale-125 object-cover blur-lg saturate-[0.9] select-none",
                      selectedIsFinished && "grayscale"
                    )}
                  />
                )}
              </AnimatePresence>
              <div className="dock-cover-shade absolute inset-0" />
            </div>
            {/* Same pop-in as the desktop pill's cover when a new campaign is picked: the whole
            identity (cover + title + timer) springs up from the bar's baseline. */}
            {/* Keyed on the campaign: the old identity is swapped out instantly and the new one
                springs up from the bar's baseline — no exit phase, so the bar never sits empty. */}
            <motion.div
              key={selectedCampaign.campaign.id}
              initial={{ scaleY: 0.9, y: 8, opacity: 0 }}
              animate={{ scaleY: 1, y: 0, opacity: 1, originY: 1 }}
              transition={switcherSpring}
              className="flex min-w-0 flex-1 items-center gap-3"
            >
              <CampaignImage
                campaign={selectedCampaign.campaign}
                hideTitle
                hideDuration
                className={cn(
                  "size-10 shrink-0 rounded-full border border-white/15",
                  selectedIsFinished && "opacity-70 grayscale"
                )}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                {/* Two lines, like the desktop card: name + time left on the first, the hook on
                    the second. The name truncates here (the bar is 64px; the desktop pill does
                    the same), the prize never does — it gets the whole second line and glides. */}
                <div className="flex min-w-0 items-center gap-2">
                  <Typography
                    tag="span"
                    className={cn(
                      "min-w-0 flex-1 truncate text-[13px] leading-tight font-bold tracking-wide",
                      selectedIsFinished
                        ? "text-muted-foreground"
                        : "text-foreground"
                    )}
                  >
                    {campaignDisplayTitle(selectedCampaign.campaign, t)}
                  </Typography>
                </div>
                {selectedHook && (
                  <CampaignHookLine hook={selectedHook} className="min-w-0" />
                )}
              </div>
            </motion.div>
            <SwitcherCoverStack
              others={otherCampaigns}
              attention={attention}
              open={isOpen}
              nudge={hint === "peek"}
            />
          </button>
        </motion.div>
      </div>
    </>
  );
};

const HINT_KEY = "unibo-igw-switcher-hint";
const NEWS_KEY = "unibo-igw-switcher-news";

const readSession = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeSession = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Private mode / blocked storage: the hint just shows again next time.
  }
};
const newsId = (key: string, a: CampaignAttention) =>
  `${key}:${a.tone}:${a.label}`;
const seenNews = (): string[] => {
  try {
    return JSON.parse(readSession(NEWS_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
};
const markNewsSeen = (id: string) =>
  writeSession(NEWS_KEY, JSON.stringify([...seenNews(), id].slice(-20)));

/** One line over the cover stack announcing another campaign's news — once per piece of news per
 *  session. A button: tapping it goes straight to that campaign. */
const NewsBubble = ({
  title,
  attention,
  onSelect,
}: {
  title: string;
  attention: CampaignAttention;
  onSelect: () => void;
}) => (
  <motion.button
    type="button"
    onClick={onSelect}
    initial={{ opacity: 0, y: 8, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: 4, transition: { duration: 0.15 } }}
    transition={switcherSpring}
    className={cn(
      "notification-shadow absolute right-3 bottom-full mb-3 flex max-w-[calc(100%-1.5rem)] cursor-pointer items-center gap-2 rounded-xl border bg-background-card-surface px-3 py-2 text-left font-heading text-[12px] leading-4 font-semibold text-foreground",
      attention.tone === "urgent" ? "border-urgent/55" : "border-prizes/55"
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        "size-2 shrink-0 rounded-full",
        attention.tone === "urgent" ? "bg-urgent" : "bg-prizes"
      )}
    />
    <span className="min-w-0 truncate">
      {title} · {attention.label}
    </span>
  </motion.button>
);
